import { DeepNullablePartial, ErrorCode } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { backend, domain, stateTree } from 'modules/data';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { amplitudeService } from 'services/amplitude';
import { api } from 'services/api';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { hasChangesState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { getEditCommands, validateRequest } from '../../../selectors/requestSelectors';
import { getXeroBillBatchPaymentRequestTransfer } from '../../../selectors/xero';
import { isModifiedRequestState } from '../../../states';
import { messages } from './XeroBillBatchPaymentForm.messages';

export const useSaveAndSubmitMutation = (
    xeroBillBatchPayment: domain.XeroBillBatchPayment,
    request: domain.XeroBillBatchPaymentRequest,
    setBillInvoiceRequestIdsWithExceededAmount: (billInvoiceRequestIdsWithExceededAmount: string[]) => void,
    setBillInvoiceRequestIdsWithError: (billInvoiceRequestIdsWithError: string[]) => void
) => {
    const [isSaving, setIsSaving] = useState(false);
    const commands = useSelector(getEditCommands);
    const setIsModifiedRequest = useSetAtom(isModifiedRequestState);
    const setHasChanges = useSetAtom(hasChangesState);

    const { mutate } = useMutation(async (transfer: DeepNullablePartial<backend.transfers.RequestEditTransfer>) => {
        try {
            setIsSaving(true);
            setBillInvoiceRequestIdsWithExceededAmount([]);
            setBillInvoiceRequestIdsWithError([]);
            setIsModifiedRequest(true);
            setHasChanges(false);

            await api.requests.edit(transfer);

            if (!commands.saveAndSubmit.hidden && !commands.saveAndSubmit.disabled) {
                await api.requests.publish({
                    requestId: request.id,
                    companyId: request.companyId,
                });

                amplitudeService.sendData('requests: create request', {
                    'request type': domain.IntegrationCode.XeroBillBatchPayment.toLocaleLowerCase(),
                });

                toast.success(messages.saveAndSubmitRequestSuccess);
            }

            routingService.push(getPath(Path.request, request.id, request.companyId));
        } catch (error) {
            const errorCode = errorHelpers.getErrorCode(error);

            switch (errorCode) {
                case ErrorCode.E4158_XERO_BILL_BATCH_PAYMENT_EXCEEDED_AMOUNT: {
                    const billInvoiceRequestIdsWithExceededAmount = error.billInvoiceRequestIds;

                    setBillInvoiceRequestIdsWithExceededAmount(billInvoiceRequestIdsWithExceededAmount);
                    break;
                }

                case ErrorCode.E4159_XERO_BILL_BATCH_PAYMENT_DIFFERENT_CURRENCY:
                case ErrorCode.E4161_XERO_BILL_BATCH_PAYMENT_INCORRECT_STATUS: {
                    const billInvoiceRequestIdsWithError = error.billInvoiceRequestIds;

                    setBillInvoiceRequestIdsWithError(billInvoiceRequestIdsWithError);
                    break;
                }
            }
        } finally {
            setIsSaving(false);
        }
    });

    const saveAndSubmit = useCallback(
        (commentText: string) => {
            const details = {
                ...request.details,
                ...xeroBillBatchPayment,
            } as domain.XeroBillBatchPaymentDetails;
            const state = window.ApprovalMax.app.getStore().getState() as stateTree.State;
            const requestWithBillBatchPayment = { ...request, details };

            const errors = validateRequest(state, requestWithBillBatchPayment);

            if (errors.length > 0) {
                errors.forEach((error) => {
                    notificationService.showErrorToast(error);
                });

                return;
            }

            const transfer = getXeroBillBatchPaymentRequestTransfer(
                window.ApprovalMax.app.getStore().getState() as stateTree.State,
                requestWithBillBatchPayment,
                commentText
            );

            mutate(transfer);
        },
        [mutate, request, xeroBillBatchPayment]
    );

    return { saveAndSubmit, isSaving };
};
